<template>
  <DefaultLayout>
    <div class="page-header">
      <div>
        <h1>Gestión de Prompts y Conocimiento</h1>
        <p class="subtitle">Administra los prompts del sistema y las bases de conocimiento de cada agente</p>
      </div>
    </div>

    <!-- Selector de agente -->
    <Card class="agent-selector-card">
      <template #content>
        <div class="agent-selector">
          <label class="selector-label">Agente:</label>
          <Dropdown
            v-model="selectedAgentId"
            :options="agents"
            optionLabel="name"
            optionValue="id"
            placeholder="Selecciona un agente"
            class="agent-dropdown"
            @change="onAgentChange"
          >
            <template #option="slotProps">
              <div class="agent-option">
                <span class="agent-name">{{ slotProps.option.name }}</span>
                <Tag :value="slotProps.option.category" severity="secondary" size="small" />
              </div>
            </template>
          </Dropdown>
          <div v-if="selectedAgentId" class="agent-info">
            <Tag :value="`v${currentVersion}`" severity="info" size="small" />
            <span class="prompt-length">{{ promptLength }} caracteres</span>
          </div>
        </div>
      </template>
    </Card>

    <!-- Tabs -->
    <TabView v-if="selectedAgentId" v-model:activeIndex="activeTab" class="management-tabs">
      <!-- Tab 1: Editor de Prompt -->
      <TabPanel header="Prompt del Sistema">
        <PromptEditor
          :agent-id="selectedAgentId"
          :agent-name="selectedAgentName"
          :initial-prompt="currentPrompt"
          :variables="availableVariables"
          @saved="onPromptSaved"
          @version-changed="onVersionChanged"
        />
      </TabPanel>

      <!-- Tab 2: Bases de Conocimiento -->
      <TabPanel header="Bases de Conocimiento (RAG)">
        <KnowledgeBaseManager :agent-id="selectedAgentId" />
      </TabPanel>

      <!-- Tab 3: Historial de Versiones -->
      <TabPanel header="Historial de Versiones">
        <div class="version-history">
          <DataTable
            :value="versions"
            :loading="loadingVersions"
            responsiveLayout="scroll"
            stripedRows
            class="versions-table"
          >
            <Column field="version" header="Versión" style="width: 80px">
              <template #body="{ data }">
                <Tag :value="`v${data.version}`" :severity="data.version === currentVersion ? 'success' : 'secondary'" />
              </template>
            </Column>
            <Column field="change_note" header="Nota de cambio" />
            <Column field="created_at" header="Fecha" style="width: 180px">
              <template #body="{ data }">
                {{ formatDate(data.created_at) }}
              </template>
            </Column>
            <Column header="Acciones" style="width: 120px">
              <template #body="{ data }">
                <Button
                  v-if="data.version !== currentVersion"
                  icon="pi pi-refresh"
                  label="Restaurar"
                  text
                  size="small"
                  severity="warning"
                  @click="confirmRollback(data)"
                />
                <Button
                  v-else
                  icon="pi pi-check"
                  label="Actual"
                  text
                  size="small"
                  disabled
                />
              </template>
            </Column>
          </DataTable>
        </div>
      </TabPanel>
    </TabView>

    <!-- Empty state -->
    <div v-else class="empty-state">
      <i class="pi pi-cog" style="font-size: 3rem; color: #d1d5db;" />
      <p>Selecciona un agente para gestionar sus prompts y bases de conocimiento.</p>
    </div>

    <!-- Confirm rollback dialog -->
    <Dialog v-model:visible="showRollbackConfirm" header="Restaurar versión" modal :style="{ width: '400px' }">
      <p>¿Restaurar el prompt a la <strong>versión {{ rollbackTarget?.version }}</strong>?</p>
      <p class="rollback-note">Se creará una nueva versión con el contenido de la versión seleccionada.</p>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showRollbackConfirm = false" />
        <Button label="Restaurar" severity="warning" :loading="rollingBack" @click="executeRollback" />
      </template>
    </Dialog>

    <Toast />
  </DefaultLayout>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { useToast } from 'primevue/usetoast';
import Card from 'primevue/card';
import Dropdown from 'primevue/dropdown';
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import Toast from 'primevue/toast';
import DefaultLayout from '../../components/layout/DefaultLayout.vue';
import PromptEditor from '../../components/admin/PromptEditor.vue';
import KnowledgeBaseManager from '../../components/admin/KnowledgeBaseManager.vue';
import api from '../../services/api';

const toast = useToast();

const agents = ref([]);
const selectedAgentId = ref(null);
const activeTab = ref(0);
const currentPrompt = ref('');
const currentVersion = ref(0);
const promptLength = computed(() => currentPrompt.value.length);
const versions = ref([]);
const loadingVersions = ref(false);

const showRollbackConfirm = ref(false);
const rollbackTarget = ref(null);
const rollingBack = ref(false);

const availableVariables = [
  { key: '{{tenant_name}}', description: 'Nombre del tenant/empresa actual' },
  { key: '{{agent_name}}', description: 'Nombre del agente' },
  { key: '{{agent_category}}', description: 'Categoría del agente' },
  { key: '{{user_name}}', description: 'Nombre del usuario' },
  { key: '{{current_date}}', description: 'Fecha actual' },
  { key: '{{rag_context}}', description: 'Contexto RAG recuperado de las KBs del agente' },
];

const selectedAgentName = computed(() => {
  const agent = agents.value.find(a => a.id === selectedAgentId.value);
  return agent?.name || '';
});

const loadAgents = async () => {
  try {
    const res = await api.get('/admin/agents-prompts');
    agents.value = res.data;
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los agentes', life: 3000 });
  }
};

const onAgentChange = async () => {
  if (!selectedAgentId.value) return;
  await loadPromptData();
};

const loadPromptData = async () => {
  try {
    const res = await api.get(`/admin/agents/${selectedAgentId.value}/prompt`);
    currentPrompt.value = res.data.prompt;
    currentVersion.value = res.data.current_version;
    await loadVersions();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.response?.data?.detail || e.message, life: 3000 });
  }
};

const loadVersions = async () => {
  loadingVersions.value = true;
  try {
    const res = await api.get(`/admin/agents/${selectedAgentId.value}/prompt/versions`);
    versions.value = res.data;
  } catch (e) {
    console.error(e);
  } finally {
    loadingVersions.value = false;
  }
};

const onPromptSaved = (data) => {
  currentPrompt.value = data.prompt;
  currentVersion.value = data.version;
  loadVersions();
};

const onVersionChanged = () => {
  loadPromptData();
};

const confirmRollback = (versionData) => {
  rollbackTarget.value = versionData;
  showRollbackConfirm.value = true;
};

const executeRollback = async () => {
  if (!rollbackTarget.value) return;
  rollingBack.value = true;
  try {
    await api.post(`/admin/agents/${selectedAgentId.value}/prompt/rollback/${rollbackTarget.value.version}`);
    toast.add({ severity: 'success', summary: 'Restaurado', detail: `Prompt restaurado a v${rollbackTarget.value.version}`, life: 2500 });
    showRollbackConfirm.value = false;
    await loadPromptData();
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.response?.data?.detail || e.message, life: 3000 });
  } finally {
    rollingBack.value = false;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleString('es-CO', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

onMounted(loadAgents);
</script>

<style scoped>
.page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 1.5rem; }
.page-header h1 { margin: 0; font-size: 1.5rem; color: #1e293b; }
.subtitle { margin: 0.25rem 0 0; color: #6b7280; font-size: 0.9rem; }

.agent-selector-card { margin-bottom: 1.5rem; }
.agent-selector { display: flex; align-items: center; gap: 1rem; flex-wrap: wrap; }
.selector-label { font-weight: 600; color: #374151; font-size: 0.9rem; }
.agent-dropdown { min-width: 300px; }
.agent-option { display: flex; align-items: center; gap: 0.5rem; }
.agent-name { font-weight: 500; }
.agent-info { display: flex; align-items: center; gap: 0.75rem; margin-left: auto; }
.prompt-length { font-size: 0.8rem; color: #9ca3af; }

.management-tabs { margin-top: 0.5rem; }

.empty-state { text-align: center; padding: 4rem 2rem; color: #9ca3af; }
.empty-state p { margin-top: 1rem; }

.version-history { padding: 1rem 0; }
.rollback-note { font-size: 0.85rem; color: #6b7280; margin-top: 0.5rem; }

.versions-table { font-size: 0.9rem; }
</style>
